/**
 * GST state codes.
 *
 * Bundled as a static table rather than fetched or free-typed. Place of
 * supply decides INTRA vs INTER, which decides CGST+SGST vs IGST — so an
 * unrecognised state must be impossible to store, not merely unlikely.
 *
 * 1.6.2 defaulted a missing company state to the literal 'Telangana'
 * (js/invoice.js:255), silently issuing CGST/SGST where IGST was owed. That
 * fallback does not exist here.
 */

export interface GstState {
  /** Two-digit GST state code, as it appears in a GSTIN. */
  code: string;
  name: string;
}

export const GST_STATES: readonly GstState[] = [
  { code: '01', name: 'Jammu and Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '25', name: 'Daman and Diu' },
  { code: '26', name: 'Dadra and Nagar Haveli and Daman and Diu' },
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman and Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' },
  { code: '96', name: 'Foreign Country' },
  { code: '97', name: 'Other Territory' },
] as const;

const BY_CODE: ReadonlyMap<string, string> = new Map(
  GST_STATES.map((s) => [s.code, s.name]),
);

const BY_NAME: ReadonlyMap<string, string> = new Map(
  GST_STATES.map((s) => [s.name.toLowerCase(), s.code]),
);

/** True when `code` is a real GST state code. */
export function isValidStateCode(code: string): boolean {
  return BY_CODE.has(code);
}

/** Display name for a state code, or the code itself if unknown. */
export function stateNameFor(code: string): string {
  return BY_CODE.get(code) ?? code;
}

/**
 * Resolve a state from whatever 1.6.x happened to store — a code, a display
 * name in any case, or padded digits.
 * Returns null when it cannot be resolved, so the caller can ask the user
 * rather than guess.
 */
export function resolveStateCode(input: string): string | null {
  const raw = String(input ?? '').trim();
  if (!raw) return null;
  if (BY_CODE.has(raw)) return raw;

  const byName = BY_NAME.get(raw.toLowerCase());
  if (byName) return byName;

  // "Telangana State", "Telangana." and similar variants
  const loose = raw.toLowerCase().replace(/\b(state|state\.|state name)\b/g, '').replace(/[.\s]+/g, ' ').trim();
  const looseMatch = BY_NAME.get(loose);
  if (looseMatch) return looseMatch;

  const digits = raw.replace(/\D/g, '').padStart(2, '0').slice(0, 2);
  return BY_CODE.has(digits) ? digits : null;
}

/**
 * Derive the state code from a GSTIN's first two characters.
 * 1.6.x stored an EMPTY `company.state` on real records, so for those the
 * GSTIN is the only available source of place of supply.
 */
export function stateCodeFromGstin(gstin: string): string | null {
  const code = String(gstin ?? '').trim().slice(0, 2);
  return BY_CODE.has(code) ? code : null;
}

/** Shape check only — does not verify the checksum. */
export function isGstinShaped(gstin: string): boolean {
  return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(
    String(gstin ?? '').trim().toUpperCase(),
  );
}
